import React, { useEffect, useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import RichContent from "@/components/RichContent";
import publicApi from "@/lib/publicApi";
import usePageContent, { s } from "@/hooks/usePageContent";
import { localePath, parseLocaleFromPath } from "@/lib/locale";
import CatalogueCallout from "@/components/CatalogueCallout";

// Product id → URL slug. Kept in sync with backend `product_types.slug_path`.
const SLUG_MAP = {
  prim: "primovi",
  bisernica: "bisernice",
  brac: "bracevi",
  bugarija: "bugarije",
  celo: "cela",
  berda: "berde",
};

export default function Models() {
  const { t } = useTranslation();
  const location = useLocation();
  const { locale } = parseLocaleFromPath(location.pathname);
  const { data: page } = usePageContent("models", locale);
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    publicApi.get("/public/product-types", { params: { locale } }).then((r) => setProducts(r.data)).catch(() => setProducts([]));
  }, [locale]);

  const filtered = useMemo(
    () => (filter === "all" ? products : products.filter((p) => p.id === filter)),
    [products, filter]
  );

  return (
    <>
      <SEO title={page?.meta_title} description={page?.meta_description} />
      <section className="bg-background grain-bg">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-24">
          <div className="max-w-2xl">
            <div className="text-[11px] uppercase tracking-[0.3em] text-primary mb-4">{s(page, "eyebrow")}</div>
            <h1 className="font-serif text-5xl lg:text-6xl text-foreground leading-[1.05]">{s(page, "title")}</h1>
            <p className="mt-5 text-lg text-foreground/75">{s(page, "subtitle")}</p>
          </div>

          {/* Terracotta callout banner (contact-first messaging) */}
          <div className="mt-10">
            <CatalogueCallout testid="models-catalogue-callout" />
          </div>

          <div className="mt-10 flex flex-wrap gap-2" data-testid="models-filters">
            <FilterBtn active={filter === "all"} onClick={() => setFilter("all")} testid="filter-all">
              {s(page, "filter_all") || t("pages:models.filter_all")}
            </FilterBtn>
            {products.map((p) => (
              <FilterBtn
                key={p.id}
                active={filter === p.id}
                onClick={() => setFilter(p.id)}
                testid={`filter-${p.id}`}
              >
                {p.name}
              </FilterBtn>
            ))}
          </div>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="models-grid">
            {filtered.map((p) => (
              <Link
                key={p.id}
                to={localePath(locale, `/${SLUG_MAP[p.id] || p.id}`)}
                data-testid={`model-card-${p.id}`}
                className="group block bg-card rounded-lg border border-border/60 overflow-hidden shadow-ambient hover:shadow-interactive transition-shadow"
              >
                <div className="aspect-[5/4] overflow-hidden bg-muted">
                  <img src={p.hero_image_url} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700" />
                </div>
                <div className="p-6">
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="font-serif text-2xl text-foreground">{p.name}</h3>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.tagline}</p>
                  <p className="mt-4 text-sm text-foreground/70 line-clamp-2">{p.description}</p>
                  <div className="mt-5 inline-flex items-center gap-1.5 text-sm text-primary group-hover:gap-3 transition-all">
                    {t("actions.contact_us")} <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function FilterBtn({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2 rounded-full text-sm transition-colors ${
        active ? "bg-primary text-primary-foreground shadow-interactive" : "border border-border text-foreground/75 hover:text-primary hover:border-primary/50"
      }`}
    >
      {children}
    </button>
  );
}
